import { logger } from "~/utils/logger";

interface FlipbookOptions {
  container: HTMLElement;
  pdfUrl: string;
  onPageChange?: (page: number) => void;
}

export function use3DFlipbook(options: FlipbookOptions) {
  let flipbookInstance: any = null;
  let containerElement: HTMLElement | null = options.container;
  const isDev = process.env.NODE_ENV === "development";

  // Helper to get the current flipbook instance
  function getFlipbookInstance() {
    return flipbookInstance;
  }

  function getController() {
    return flipbookInstance?.ctrl || flipbookInstance;
  }

  function loadScripts(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if jQuery is already loaded
      if (!(window as any).jQuery) {
        const jquery = document.createElement("script");
        jquery.src = "/flipbook/js/jquery.min.js";
        jquery.onload = () => {
          loadOtherScripts().then(resolve).catch(reject);
        };
        jquery.onerror = reject;
        document.head.appendChild(jquery);
      } else {
        loadOtherScripts().then(resolve).catch(reject);
      }
    });
  }

  function loadOtherScripts(): Promise<void> {
    return new Promise((resolve, reject) => {
      const scripts = [
        "/flipbook/js/html2canvas.min.js",
        "/flipbook/js/three.min.js",
        "/flipbook/js/pdf.min.js",
        "/flipbook/js/3dflipbook.min.js",
      ];

      // Load scripts sequentially to ensure proper dependency order
      const loadNextScript = (index: number) => {
        if (index >= scripts.length) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = scripts[index] ?? "";
        script.onload = () => {
          if (isDev) logger.log(`Loaded: ${scripts[index] ?? ""}`);
          loadNextScript(index + 1);
        };
        script.onerror = () =>
          reject(new Error(`Failed to load ${scripts[index] ?? ""}`));
        document.head.appendChild(script);
      };

      loadNextScript(0);
    });
  }

  function loadStyles(): void {
    const styles = [
      "/flipbook/css/font-awesome.min.css",
      "/flipbook/css/white-book-view.css",
    ];

    styles.forEach((href) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      }
    });
  }

  async function init() {
    try {
      loadStyles();

      // Configure PDF.js worker and canvas settings BEFORE loading scripts
      if (typeof window !== "undefined") {
        (window as any).PDFJS = (window as any).PDFJS || {};
        (window as any).PDFJS.workerSrc = "/flipbook/js/pdf.worker.js";

        // Add global error handling for canvas operations
        const originalGetContext = HTMLCanvasElement.prototype.getContext;
        HTMLCanvasElement.prototype.getContext = function (
          contextId: any,
          options?: any,
        ): any {
          try {
            return originalGetContext.apply(this, [contextId, options] as any);
          } catch (e) {
            if (isDev) logger.warn("Canvas context error caught:", e);
            return null;
          }
        };
      }

      await loadScripts();

      const $ = (window as any).jQuery;

      // Also set the GlobalWorkerOptions after loading with enhanced error handling
      if ((window as any).pdfjsLib) {
        (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc =
          "/flipbook/js/pdf.worker.js";

        // Disable text layer to reduce canvas operations
        (window as any).pdfjsLib.disableTextLayer = true;
        (window as any).pdfjsLib.disableAutoFetch = false;
        (window as any).pdfjsLib.disableStream = false;
      }
      if ((window as any).PDFJS) {
        (window as any).PDFJS.workerSrc = "/flipbook/js/pdf.worker.js";
        (window as any).PDFJS.disableTextLayer = true;
      }

      // Initialize the flipbook and capture return value with error handling
      containerElement = options.container;
      const $container = $(options.container);

      try {
        const initResult = $container.FlipBook({
          pdf: options.pdfUrl,
          template: {
            html: "/flipbook/templates/default-book-view.html",
            styles: ["/flipbook/css/white-book-view.css"],
            links: [
              {
                rel: "stylesheet",
                href: "/flipbook/css/font-awesome.min.css",
              },
            ],
            script: "/flipbook/js/default-book-view.js",
            // Disable sounds to avoid NotAllowedError (browser autoplay policy)
            sounds: {},
          },
          controlsProps: {
            scale: {
              default: 1,
            },
          },
          // This ensures the cover shows as a single page
          cover: true,
          // Enhanced page callback with error handling
          pageCallback: function (n: number) {
            try {
              if (options.onPageChange) {
                options.onPageChange(n + 1);
              }
            } catch (error) {
              if (isDev) logger.error("Page callback error:", error);
            }
            return undefined;
          },
          // Add error callback to handle PDF loading errors
          pdfLoadingError: function (error: any) {
            if (isDev) {
              logger.error("PDF loading error (suppressed):", error);
            }
            // Error is logged but not thrown to prevent crashes
          },
        });

        // The instance IS the return value!
        flipbookInstance = initResult;
      } catch (pdfError: any) {
        // Catch InvalidPDFException and other PDF loading errors
        if (isDev) {
          logger.error("PDF initialization error (suppressed):", pdfError);
        }
        // Rethrow only if it's not a PDF-related error
        if (
          !pdfError?.message?.includes("PDF") &&
          !pdfError?.name?.includes("PDF")
        ) {
          throw pdfError;
        }
        // For PDF errors, we silently handle them
      }
    } catch (error) {
      if (isDev) logger.error("Failed to initialize 3D flipbook:", error);
      throw error;
    }
  }

  function destroy() {
    try {
      const instance = getFlipbookInstance();
      if (instance) {
        const $ = (window as any).jQuery;

        // Properly dispose of the flipbook instance
        if (instance.dispose) {
          instance.dispose();
        }

        // Clean up the container
        if (containerElement) {
          $(containerElement).empty();
        }
      }
    } catch (error) {
      if (isDev) logger.error("Error during flipbook destroy:", error);
    } finally {
      flipbookInstance = null;
      containerElement = null;
    }
  }

  function goToPage(pageIndex: number) {
    const ctrl = getController();
    if (ctrl && ctrl.goToPage) {
      ctrl.goToPage(pageIndex - 1);
    }
  }

  // Helper function to click controls in the iframe
  function clickControl(selector: string) {
    if (!containerElement) return false;

    const iframe = containerElement.querySelector("iframe");
    if (!iframe) return false;

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return false;

    const control = iframeDoc.querySelector(selector);
    if (control && control instanceof HTMLElement) {
      control.click();
      return true;
    }

    return false;
  }

  function nextPage() {
    const ctrl = getController();
    if (ctrl && ctrl.cmdForward) {
      ctrl.cmdForward();
    } else {
      // Fallback: click the forward button in the iframe
      clickControl(".cmdForward");
    }
  }

  function prevPage() {
    const ctrl = getController();
    if (ctrl && ctrl.cmdBackward) {
      ctrl.cmdBackward();
    } else {
      // Fallback: click the backward button in the iframe
      clickControl(".cmdBackward");
    }
  }

  function getCurrentPage() {
    const ctrl = getController();
    if (ctrl && ctrl.getPage) {
      const page = ctrl.getPage();
      logger.log("getCurrentPage - raw value:", page);
      // Library uses 0-based indexing, so add 1 for display
      return page + 1;
    }
    return 1;
  }

  function getTotalPages() {
    const instance = getFlipbookInstance();

    // Try multiple locations where page count might be stored
    let totalPages = 0;

    if (instance?.search?.pages) {
      totalPages = instance.search.pages;
      logger.log("getTotalPages from search.pages:", totalPages);
    } else if (instance?.book?.pageManager?.pages) {
      totalPages = instance.book.pageManager.pages;
      logger.log("getTotalPages from book.pageManager.pages:", totalPages);
    } else if (instance?.propsBuilder?.pdf?.numPages) {
      totalPages = instance.propsBuilder.pdf.numPages;
      logger.log("getTotalPages from propsBuilder.pdf.numPages:", totalPages);
    }

    logger.log("getTotalPages final:", totalPages);
    return totalPages;
  }

  function zoomIn() {
    const ctrl = getController();
    if (ctrl && ctrl.cmdZoomIn) {
      ctrl.cmdZoomIn();
    } else {
      // Fallback: click the zoom in button
      clickControl(".cmdZoomIn");
    }
  }

  function zoomOut() {
    const ctrl = getController();
    if (ctrl && ctrl.cmdZoomOut) {
      ctrl.cmdZoomOut();
    } else {
      // Fallback: click the zoom out button
      clickControl(".cmdZoomOut");
    }
  }

  function toggleFullscreen() {
    const ctrl = getController();
    if (ctrl && ctrl.cmdFullScreen) {
      ctrl.cmdFullScreen();
    } else {
      // Fallback: click fullscreen button
      clickControl(".cmdFullScreen");
    }
  }

  return {
    init,
    destroy,
    goToPage,
    nextPage,
    prevPage,
    getCurrentPage,
    getTotalPages,
    zoomIn,
    zoomOut,
    toggleFullscreen,
  };
}
